import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ControleSituacaoService } from '@/services/ControleSituacaoService';
import { ControleSituacaoUpdate } from '@/types/controle-situacoes';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook para buscar todas as situações de controle
 */
export function useControleSituacoes() {
  return useQuery({
    queryKey: ['controle-situacoes'],
    queryFn: () => ControleSituacaoService.getAll(),
  });
}

/**
 * Hook para atualizar uma situação de controle
 */
export function useUpdateControleSituacao() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ControleSituacaoUpdate }) =>
      ControleSituacaoService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controle-situacoes'] });
      toast({
        title: 'Sucesso',
        description: 'Situação atualizada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar situação.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para atualizar múltiplas situações (edição em massa)
 */
export function useUpdateControleSituacoesMany() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (updates: Array<{ id: string; data: ControleSituacaoUpdate }>) =>
      ControleSituacaoService.updateMany(updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['controle-situacoes'] });
      toast({
        title: 'Sucesso',
        description: `${variables.length} situação(ões) atualizada(s) com sucesso.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar situações.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook para criar uma situação de controle
 */
export function useCreateControleSituacao() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (situacao: {
      tipo_situacao: string;
      valor_base_mensal: number;
      valor_por_dependente: number;
      ativo?: boolean;
    }) => ControleSituacaoService.create(situacao),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controle-situacoes'] });
      toast({
        title: 'Sucesso',
        description: 'Situação criada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao criar situação.',
        variant: 'destructive',
      });
    },
  });
}
